import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HandshakeIcon, Truck, Phone as PhoneIcon, AlertTriangle, CreditCard, Landmark, Copy, Check } from "lucide-react";
import { digitsOnly } from "@/components/EditProfileCard";
import { cardName } from "@/lib/api";

export default function Checkout() {
  const { cart, refresh } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [provinces, setProvinces] = useState([]);
  const [addr, setAddr] = useState({
    receiver_name: user?.name || "",
    receiver_cedula: user?.cedula || "",
    address_line: "", city: user?.city || "", reference: "",
    phone: user?.phone || "",
  });
  // Light-KYC (buyers): cédula only asked here when the profile doesn't have
  // one yet. If the account already has one from prior activity, we skip it.
  const [buyerCedula, setBuyerCedula] = useState(user?.cedula || "");
  const cedulaKnown = !!(user?.cedula || "").trim();
  const [buyerProvince, setBuyerProvince] = useState(user?.province || "");
  const [delivery, setDelivery] = useState({});
  const [meetupPoints, setMeetupPoints] = useState({});
  const [disclaimerOk, setDisclaimerOk] = useState(false);
  const [loading, setLoading] = useState(false);
  // Método de pago — "payphone" (tarjeta) o "transfer" (transferencia
  // bancaria manual a HitTCG con verificación del admin). Ambos casos el
  // dinero pasa por HitTCG (escrow). En transfer NO se cobra la comisión
  // Payphone (5.75%), pero el admin debe cotejar el depósito antes de que
  // el pedido pase a PAGADO.
  const [paymentMethod, setPaymentMethod] = useState("payphone");
  const [publicConfig, setPublicConfig] = useState(null);

  useEffect(() => {
    api.get("/config/public").then((r) => setPublicConfig(r.data)).catch(() => setPublicConfig(null));
  }, []);
  const hittcgBank = publicConfig?.hittcg_bank_info;
  const interbankFee = Number(publicConfig?.interbank_transfer_fee ?? 0.40);
  const bankReady = !!(hittcgBank && (hittcgBank.bank_name || "").trim() && (hittcgBank.account_number || "").trim());

  useEffect(() => {
    api.get("/geo/provinces").then((r) => setProvinces(r.data)).catch(() => {});
    try {
      const d = JSON.parse(sessionStorage.getItem("hittcg_cart_delivery") || "{}");
      setDelivery(d);
    } catch {/* noop */}
  }, []);

  const upd = (k, v) => setAddr((a) => ({ ...a, [k]: v }));
  const updMeetup = (sid, v) => setMeetupPoints((m) => ({ ...m, [sid]: v }));

  // Delivery mode composition
  const groupMethod = (g) => delivery[g.seller.id] || (g.shipping_available === false && g.meetup_available ? "meetup" : "shipping");
  const meetupGroups = cart.groups.filter((g) => groupMethod(g) === "meetup");
  const shippingGroups = cart.groups.filter((g) => groupMethod(g) !== "meetup");
  const hasMeetup = meetupGroups.length > 0;
  const hasShipping = shippingGroups.length > 0;

  // Client-side Ecuadorian cédula validation (modulus-10). Backend re-validates.
  const isValidCedula = (c) => {
    const v = (c || "").trim();
    if (!/^\d{10}$/.test(v)) return false;
    const prov = parseInt(v.slice(0, 2), 10);
    if (!((prov >= 1 && prov <= 24) || prov === 30)) return false;
    if (parseInt(v[2], 10) >= 6) return false;
    const w = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    let total = 0;
    for (let i = 0; i < 9; i++) {
      let p = parseInt(v[i], 10) * w[i];
      if (p > 9) p -= 9;
      total += p;
    }
    const check = (10 - (total % 10)) % 10;
    return check === parseInt(v[9], 10);
  };
  const isValidRuc = (r) => {
    const v = (r || "").trim();
    if (!/^\d{13}$/.test(v)) return false;
    if (!v.endsWith("001")) return false;
    const third = parseInt(v[2], 10);
    if (third < 6) return isValidCedula(v.slice(0, 10));
    return third === 6 || third === 9;
  };
  const buyerIdOk = cedulaKnown || isValidCedula(buyerCedula) || isValidRuc(buyerCedula);

  const submit = async (e) => {
    e.preventDefault();
    if (!buyerProvince) { toast.error("Selecciona tu provincia"); return; }
    const localBlocked = cart.groups.find((g) => g.local_only_province && g.local_only_province !== buyerProvince);
    if (localBlocked) { toast.error(`${localBlocked.seller?.name || "Un vendedor"} solo envía dentro de ${localBlocked.local_only_province}. Quita sus productos del carrito para continuar.`); return; }
    if (!addr.phone.trim()) { toast.error("Necesitamos tu teléfono"); return; }
    if (!cedulaKnown && !buyerIdOk) {
      toast.error("Ingresa un número de cédula ecuatoriana válido (10 dígitos) o RUC (13 dígitos)");
      return;
    }
    if (hasShipping) {
      if (!addr.receiver_name.trim() || !addr.address_line.trim() || !addr.city.trim()) {
        toast.error("Completa los datos de envío"); return;
      }
    }
    if (hasMeetup && !disclaimerOk) {
      toast.error("Debes aceptar los términos del encuentro presencial");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/orders/checkout-cart", {
        address: addr,
        delivery_methods: Object.fromEntries(cart.groups.map((g) => [g.seller.id, groupMethod(g)])),
        buyer_province: buyerProvince,
        meetup_points: meetupPoints,
        meetup_disclaimer_accepted: hasMeetup ? disclaimerOk : false,
        buyer_cedula: cedulaKnown ? undefined : buyerCedula.trim(),
        payment_method: paymentMethod,
      });
      await refresh();
      if (r.data.payment_method === "transfer" || r.data.mode === "transfer") {
        // Transferencia: NO redirigimos a Payphone. Llevamos al comprador al
        // detalle del primer pedido, donde encuentra los datos de la cuenta
        // HitTCG y el form para subir el comprobante.
        const firstId = r.data.order_ids?.[0];
        if (firstId) {
          nav(`/order/${firstId}`);
        } else {
          nav("/buyer");
        }
      } else {
        window.location.href = r.data.checkout_url;
      }
    } catch (err) {
      // Feb 2026 — Candado B: pedido duplicado activo. Backend devuelve 409
      // con {code:"duplicate_active_order", existing_order_id, message}.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "duplicate_active_order" && detail?.existing_order_id) {
        toast.error(detail.message || "Ya tienes un pedido activo de este producto", {
          duration: 8000,
          action: {
            label: "Ver mi pedido",
            onClick: () => nav(`/order/${detail.existing_order_id}`),
          },
        });
        setLoading(false);
        return;
      }
      toast.error(detail || err.response?.data?.detail || "Error");
      setLoading(false);
    }
  };

  if (!cart.groups.length) {
    return <div className="max-w-2xl mx-auto p-8 text-neutral-500">Carrito vacío. <button onClick={() => nav("/catalog")} className="text-[#EF4444]">Ir al catálogo</button></div>;
  }

  const field = (label, k, type = "text", required = true, placeholder = "") => (
    <div key={k}>
      <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">{label}</Label>
      <Input data-testid={`checkout-${k}`} type={type} value={addr[k]} onChange={(e) => upd(k, e.target.value)} required={required} placeholder={placeholder} className="bg-[#171717] border-[#262626] text-white" />
    </div>
  );

  // Model B (seller-absorbs): buyer pays exactly card price + shipping.
  // NO gross-up. HitTCG commission + Payphone fee come out of the seller's cut.
  const displayedTotal = cart.groups.reduce((sum, g) => {
    const m = groupMethod(g);
    const ship = m === "meetup" ? 0 : Number(g.ship_cost || 0);
    return sum + Number(g.subtotal || 0) + ship;
  }, 0);
  const totalSubtotal = cart.groups.reduce((s, g) => s + Number(g.subtotal || 0), 0);
  const totalShipping = cart.groups.reduce((s, g) => s + (groupMethod(g) === "meetup" ? 0 : Number(g.ship_cost || 0)), 0);

  const canPay = !loading && (!hasMeetup || disclaimerOk) && (cedulaKnown || buyerIdOk);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-6">CHECKOUT</h1>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
        <form onSubmit={submit} className="space-y-6">
          {/* Province — always required for shipping cost + meetup validation */}
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4">
            <h2 className="font-display text-xl mb-2">Tus datos</h2>
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Tu provincia</Label>
              {/* Native <select> — Radix Select causes iOS Safari black-overlay bug */}
              <select
                data-testid="checkout-province"
                value={buyerProvince}
                onChange={(e) => setBuyerProvince(e.target.value)}
                required
                className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3"
              >
                <option value="">Selecciona tu provincia</option>
                {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="text-[11px] text-neutral-500 mt-1">Se usa para calcular el envío por provincia y validar el encuentro presencial.</div>
            </div>
            {/* Phone is required in both modes: shipping delivery contact and meetup coordination */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Teléfono</Label>
                <Input
                  data-testid="checkout-phone"
                  inputMode="numeric"
                  pattern="\d*"
                  value={addr.phone}
                  onChange={(e) => upd("phone", digitsOnly(e.target.value, 15))}
                  required
                  placeholder="Solo dígitos"
                  className="bg-[#171717] border-[#262626] text-white font-mono"
                />
              </div>
              {/* Light-KYC: cédula only shown when profile doesn't have one.
                  Payphone re-validates identity via card ownership on their end. */}
              {!cedulaKnown && (
                <div>
                  <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">
                    Cédula o RUC <span className="text-[#EF4444]">*</span>
                  </Label>
                  <Input
                    data-testid="checkout-buyer-cedula"
                    inputMode="numeric"
                    pattern="\d*"
                    value={buyerCedula}
                    onChange={(e) => setBuyerCedula(digitsOnly(e.target.value, 13))}
                    required
                    placeholder="10 dígitos (cédula) o 13 (RUC)"
                    className={`bg-[#171717] border-[#262626] text-white font-mono ${buyerCedula && !buyerIdOk ? "border-red-500" : ""}`}
                  />
                  <div className="text-[11px] text-neutral-500 mt-1">
                    Solo se usa para confirmar tu identidad ante el pago. No pedimos fotos para comprar.
                  </div>
                  {buyerCedula && !buyerIdOk && (
                    <div className="text-[11px] text-red-400 mt-1" data-testid="checkout-cedula-invalid">
                      Número inválido. Revisa que sean 10 dígitos (cédula) o 13 (RUC).
                    </div>
                  )}
                </div>
              )}
              {cedulaKnown && (
                <div>
                  <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Cédula</Label>
                  <div
                    data-testid="checkout-cedula-known"
                    className="bg-[#0A0A0A] border border-[#262626] text-neutral-400 rounded-md h-10 px-3 flex items-center font-mono text-sm"
                  >
                    {user.cedula}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shipping fields — only if at least one group ships by courier */}
          {hasShipping && (
            <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4" data-testid="checkout-shipping-section">
              <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Truck size={18} /> Datos de envío</h2>
              <p className="text-xs text-neutral-500">
                Aplica solo a los sub-pedidos con envío por courier ({shippingGroups.length} vendedor{shippingGroups.length !== 1 ? "es" : ""}).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field("Nombre del receptor", "receiver_name", "text", true, "A quién le entregan")}
                <div>
                  <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Cédula / RUC receptor (opcional)</Label>
                  <Input
                    data-testid="checkout-receiver_cedula"
                    inputMode="numeric"
                    pattern="\d*"
                    value={addr.receiver_cedula}
                    onChange={(e) => upd("receiver_cedula", digitsOnly(e.target.value, 13))}
                    placeholder="Solo dígitos (10 cédula · 13 RUC)"
                    className="bg-[#171717] border-[#262626] text-white font-mono"
                  />
                </div>
                <div className="sm:col-span-2">{field("Dirección completa", "address_line", "text", true, "Calle, número")}</div>
                {field("Ciudad", "city", "text", true, "Ej: Quito")}
                <div className="sm:col-span-2">{field("Referencia (opcional)", "reference", "text", false, "Cerca de...")}</div>
              </div>
            </div>
          )}

          {/* Meetup blocks — one per meetup group */}
          {hasMeetup && (
            <div className="bg-[#111] border border-emerald-500/30 rounded-md p-6 space-y-4" data-testid="checkout-meetup-section">
              <h2 className="font-display text-xl mb-2 flex items-center gap-2 text-emerald-300"><HandshakeIcon size={18} /> Encuentro presencial</h2>
              <p className="text-xs text-neutral-400">
                No se piden datos de envío para estos sub-pedidos. El vendedor y tú coordinan por teléfono el lugar y hora.
              </p>
              {meetupGroups.map((g) => (
                <div key={g.seller.id} className="p-3 bg-[#0A0A0A] border border-[#262626] rounded" data-testid={`checkout-meetup-${g.seller.id}`}>
                  <div className="text-sm text-white font-medium mb-1">{g.seller.name}</div>
                  <div className="text-xs text-neutral-500 mb-2">{g.seller.city}{g.seller.province ? `, ${g.seller.province}` : ""}</div>
                  {g.meetup_notes && (
                    <div className="text-xs bg-[#111] border border-[#262626] p-2 rounded mb-2">
                      <span className="text-emerald-400 font-medium">Sugerencia del vendedor: </span>
                      <span className="text-neutral-300">{g.meetup_notes}</span>
                    </div>
                  )}
                  <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-1 block">Punto de encuentro acordado (opcional)</Label>
                  <Textarea
                    data-testid={`checkout-meetup-point-${g.seller.id}`}
                    rows={2}
                    value={meetupPoints[g.seller.id] || ""}
                    onChange={(e) => updMeetup(g.seller.id, e.target.value)}
                    placeholder="Ej: Metro Norte de Quito, sábado en la mañana"
                    className="bg-[#171717] border-[#262626]"
                  />
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-400">
                    <PhoneIcon size={12} /> Los teléfonos de ambos se revelarán después del pago para coordinar.
                  </div>
                </div>
              ))}

              {/* Mandatory disclaimer */}
              <label className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/40 rounded cursor-pointer" data-testid="checkout-meetup-disclaimer">
                <input
                  type="checkbox"
                  data-testid="checkout-meetup-disclaimer-check"
                  checked={disclaimerOk}
                  onChange={(e) => setDisclaimerOk(e.target.checked)}
                  className="mt-1"
                />
                <div className="text-xs text-amber-100 leading-relaxed">
                  <div className="flex items-center gap-1 mb-1 text-amber-300 font-semibold"><AlertTriangle size={12} /> Al elegir encuentro presencial:</div>
                  Tú y el vendedor tienen <b>3 días</b> para encontrarse y concretar la entrega. Ambos deben <b>subir una foto que confirme la entrega</b>. El dinero queda <b>retenido</b> y se libera al vendedor cuando confirmes que recibiste todo bien. Si no se concreta en 3 días o hay algún problema, se abre el proceso de <b>disputa/reembolso</b>.
                </div>
              </label>
            </div>
          )}

          <div className="p-4 bg-[#0A0A0A] border border-[#262626] rounded-md text-sm text-neutral-400">
            Tu pago será <b className="text-white">retenido por HitTCG</b> hasta que confirmes que recibiste cada carta.
            {hasShipping && <> Envío: el vendedor tiene <b className="text-white">3 días</b> para enviar; de no hacerlo, podrás pedir reembolso automático.</>}
            {hasMeetup && <> Encuentro: 3 días para concretar la entrega presencial.</>}
          </div>

          {/* Selector método de pago — Payphone (tarjeta) o Transferencia.
              Ambos casos pasan por la cuenta HitTCG (escrow). Transferencia
              solo aparece si el admin ya configuró los datos bancarios. */}
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-3" data-testid="checkout-payment-method">
            <h2 className="font-display text-xl mb-1">Método de pago</h2>
            <p className="text-xs text-neutral-500">En ambos casos el dinero va a la <b className="text-white">cuenta de HitTCG</b> (retención) — nunca directo al vendedor.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label
                data-testid="pay-method-payphone"
                className={`cursor-pointer rounded-md border p-4 transition ${paymentMethod === "payphone" ? "border-[#EF4444] bg-[#EF4444]/5" : "border-[#262626] hover:border-neutral-600"}`}
              >
                <div className="flex items-start gap-3">
                  <input type="radio" name="pm" checked={paymentMethod === "payphone"} onChange={() => setPaymentMethod("payphone")} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-white font-medium"><CreditCard size={16} className="text-[#EF4444]" />Tarjeta (Payphone)</div>
                    <div className="text-xs text-neutral-400 mt-1">Cobro inmediato. Recibes la confirmación al instante.</div>
                  </div>
                </div>
              </label>
              <label
                data-testid="pay-method-transfer"
                className={`rounded-md border p-4 transition ${paymentMethod === "transfer" ? "border-[#EF4444] bg-[#EF4444]/5" : "border-[#262626] hover:border-neutral-600"} ${bankReady ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}
                title={bankReady ? undefined : "El admin aún no ha configurado la cuenta de HitTCG"}
              >
                <div className="flex items-start gap-3">
                  <input type="radio" name="pm" disabled={!bankReady} checked={paymentMethod === "transfer"} onChange={() => bankReady && setPaymentMethod("transfer")} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-white font-medium"><Landmark size={16} className="text-[#EF4444]" />Transferencia bancaria</div>
                    <div className="text-xs text-neutral-400 mt-1">Transfieres a la cuenta de HitTCG y subes el comprobante. Un admin verifica y confirma. Tienes <b>1 hora</b> para subir el comprobante.</div>
                    {!bankReady && <div className="text-[10px] text-amber-400 mt-1">No disponible ahora — pide activación al admin</div>}
                  </div>
                </div>
              </label>
            </div>
            {paymentMethod === "payphone" && (
              <div className="mt-2 p-3 bg-purple-500/5 border border-purple-500/20 rounded text-xs text-neutral-300 leading-relaxed" data-testid="checkout-payphone-info">
                Tú pagas exactamente <b className="text-white">${displayedTotal.toFixed(2)}</b> (precio + envío). Payphone cobra a la plataforma un 5.75% que <b>absorbe el vendedor</b> — no se te suma nada extra.
              </div>
            )}
            {paymentMethod === "transfer" && bankReady && (
              <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-200 leading-relaxed" data-testid="checkout-transfer-info">
                Al confirmar, verás los datos de la cuenta HitTCG en el detalle del pedido para transferir y subir el comprobante. <b>Al pagar por transferencia no aplica la comisión Payphone (5.75%)</b>, así el vendedor recibe más neto.
                {(hittcgBank.bank_name || "").toLowerCase().includes("pichincha") && <> Si transfieres desde otro banco, tu banco puede cobrarte una comisión interbancaria de ~<b>${interbankFee.toFixed(2)}</b>.</>}
              </div>
            )}
          </div>

          <Button data-testid="checkout-pay" type="submit" disabled={!canPay} className="w-full bg-[#EF4444] hover:bg-[#DC2626] h-12">
            {loading ? "Redirigiendo…" : paymentMethod === "transfer" ? `Continuar por transferencia — $${displayedTotal.toFixed(2)}` : `Pagar $${displayedTotal.toFixed(2)} con Payphone`}
          </Button>
        </form>

        <div className="bg-[#111] border border-[#262626] rounded-md p-6 h-fit">
          <div className="text-neutral-500 text-xs uppercase tracking-wider mb-3">Resumen</div>
          {cart.groups.map((g) => {
            const m = groupMethod(g);
            const ship = m === "meetup" ? 0 : g.ship_cost;
            return (
              <div key={g.seller.id} className="mb-3 pb-3 border-b border-[#262626] last:border-b-0">
                <div className="text-xs text-neutral-500 mb-1 flex items-center justify-between">
                  <span>Vendedor: {g.seller.name}</span>
                  {m === "meetup" ? (
                    <span className="text-emerald-400 inline-flex items-center gap-1"><HandshakeIcon size={10} /> Encuentro</span>
                  ) : (
                    <span className="text-neutral-400 inline-flex items-center gap-1"><Truck size={10} /> Envío</span>
                  )}
                </div>
                {g.items.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm">
                    <span className="line-clamp-1 pr-2">{cardName(it.card, it.publication?.language)} × {it.quantity}</span>
                    <span>${Number(it.publication.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>Envío</span>
                  <span>{ship === 0 ? (m === "meetup" ? "$0 (presencial)" : "GRATIS") : `$${ship.toFixed(2)}`}</span>
                </div>
              </div>
            );
          })}
          {/* Buyer summary — Model B: buyer pays exactly card price + shipping.
              No commission line is shown to the buyer (commissions are absorbed
              by the seller and only visible in the seller's dashboard). */}
          <div className="pt-2 space-y-1 text-xs text-neutral-400">
            <div className="flex justify-between"><span>Precio cartas</span><span>${totalSubtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Envío</span><span>${totalShipping.toFixed(2)}</span></div>
          </div>
          <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-[#262626]">
            <span className="font-display">TOTAL A PAGAR</span>
            <span className="font-display text-2xl text-[#EF4444]" data-testid="checkout-total">${displayedTotal.toFixed(2)}</span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
            Sin comisiones ocultas. Pagas exactamente el precio del vendedor + el envío. El pago queda retenido por HitTCG hasta que confirmes que recibiste tu carta.
          </div>
        </div>
      </div>
    </div>
  );
}
